import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Analytics, AnalyticsEvent } from '../analytics/Analytics'
import { usePotentials, useSyncStoriesToBasket, useVoucherCap } from '../basket/potentials'
import { buildSteps } from '../flow/steps'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'
import { useCheckoutStore } from '../store/checkout'
import { usePageTitleStore } from '../store/pageTitle'
import { IconLogo } from '../ui/icons'
import { hasSeenIntro, markIntroSeen } from '../util/introSeen'
import { scrollWidgetToTop } from '../util/scroll'
import { BasketSummary } from './BasketSummary'
import { CountryWarningDialog } from './CountryWarningDialog'
import { IntroDialog } from './IntroDialog'
import { MobileBasket } from './MobileBasket'
import { Stepper } from './Stepper'
import { TopbarMenu } from './TopbarMenu'

/**
 * Shared chrome for the whole flow (mockup): a sticky topbar with the brand + dot stepper, the
 * active step in the main column, and the persistent "Your trade-in" summary rail on the right.
 * The rail owns the Back / Continue nav and the reward figure. The active step is derived from
 * the route so the stepper and nav stay in sync with the dynamic step list. Route changes emit the
 * same `trackStepChange` funnel event as v1.
 */
export function FlowLayout() {
  // The brand's `tradeInComments` flag decides whether "Your story" appears (redesign §5.5).
  const storyStep = useAuthStore((s) => s.config.tradeInComments ?? false)
  const allowedCountry = useAuthStore((s) => s.config.allowedCountry)
  const shopId = useAuthStore((s) => s.shopId)
  const [introOpen, setIntroOpen] = useState(false)
  const introResolved = useRef(false)
  const pageTitle = usePageTitleStore((s) => s.title)
  const pageSubtitle = usePageTitleStore((s) => s.subtitle)
  const termsAccepted = useCheckoutStore((s) => s.termsAccepted)
  const stories = useCheckoutStore((s) => s.stories)
  const storyConsent = useCheckoutStore((s) => s.storyConsent)
  const detailsValid = useCheckoutStore((s) => s.detailsValid)
  const [advancing, setAdvancing] = useState(false)
  const { t } = useTranslation()
  const steps = buildSteps({ storyStep })
  const navigate = useNavigate()
  const location = useLocation()
  const { potentials, loading: basketLoading } = usePotentials()
  const { exceeded } = useVoucherCap()
  const itemCount = potentials.length
  // A story never outlives its product: prune stories as items leave the basket.
  useSyncStoriesToBasket()

  // Track each v2 step through the same generic navigation event as v1. Every route change also lands
  // the customer at the top of the widget — one place, so it fires exactly once per navigation
  // (Continue, Back, the empty-basket redirect, the "My trade-ins" jump) instead of each caller having
  // to remember. The initial mount is tracked but not scrolled, so entering the flow doesn't yank the
  // host page. View swaps that aren't route changes still scroll themselves.
  const didMount = useRef(false)
  useEffect(() => {
    Analytics.event(AnalyticsEvent.trackStepChange, { pathname: location.pathname })
    if (!didMount.current) {
      didMount.current = true
      return
    }
    scrollWidgetToTop()
  }, [location.pathname])

  const current = location.pathname.replace(/^\//, '')
  // Some routes are sub-pages of a step rather than steps of their own — the add-manually and
  // add-by-category pages belong to the "items" step, so they keep its stepper dot, rail and nav.
  const STEP_SUBROUTES: Record<string, string> = { add: 'items', category: 'items' }
  const stepPath = STEP_SUBROUTES[current] ?? current
  const stepIndex = steps.findIndex((s) => s.path === stepPath)
  // Off-flow routes (e.g. "My trade-ins") reuse the topbar chrome but hide the stepper, rail and nav.
  const isFlowStep = stepIndex !== -1
  const activeIndex = Math.max(0, stepIndex)

  const isFirst = activeIndex === 0
  const isLast = activeIndex === steps.length - 1
  const stepId = steps[activeIndex]?.id
  const firstPath = steps[0].path

  // The customer can remove items from the basket at any step. If they empty it while on a later
  // step there's nothing left to trade in, so send them back to the first step rather than let them
  // proceed on an empty flow. Excludes the terminal Confirm step (basket is legitimately spent
  // there) and off-flow pages, and waits for the basket to load so a transient empty state on entry
  // doesn't bounce them.
  useEffect(() => {
    if (isFlowStep && !isFirst && !isLast && !basketLoading && itemCount === 0) {
      navigate(`/${firstPath}`, { replace: true })
    }
  }, [isFlowStep, isFirst, isLast, basketLoading, itemCount, firstPath, navigate])

  // First-run intro (C2S-883): the customer's first visit to this shop's flow gets a one-time
  // explainer over the empty items step. Waits for the basket so a returning customer's items don't
  // arrive after we've already decided, and resolves ONCE per mount (`introResolved`) so dismissing it
  // and then emptying the basket doesn't re-open it. Deliberately yields to the country warning —
  // that's a real warning, and two stacked dialogs is the worst outcome here.
  useEffect(() => {
    if (introResolved.current || basketLoading || !shopId) return
    introResolved.current = true
    if (stepId !== 'items' || itemCount > 0 || allowedCountry === false) return
    if (hasSeenIntro(shopId)) return
    setIntroOpen(true)
  }, [basketLoading, shopId, stepId, itemCount, allowedCountry])

  // Only the explicit "Got it" marks the intro as seen. Dismissing it another way (scrim, Escape)
  // isn't an acknowledgement — the customer hasn't necessarily read it — so it returns next visit.
  const acceptIntro = () => {
    setIntroOpen(false)
    markIntroSeen(shopId)
  }
  // The final Confirm step has no stepper dot — it's a terminal success screen, not a stop to
  // navigate to — so it's dropped from the displayed stepper (and the stepper is hidden there).
  const stepperSteps = steps.filter((s) => s.id !== 'confirm')
  const anyStoryWritten = Object.values(stories).some((s) => s.trim().length > 0)
  // Per-step gates on Continue:
  //   basket  — at least one item on every non-terminal step (redesign §5); also guards the async
  //             empty-basket redirect above against a Continue click landing first
  //   cap     — the shop's voucher maximum must not be exceeded on any non-terminal step
  //   details — the address form must validate AND the trade-in terms be accepted
  //   story   — if any story is written, sharing consent is required (mirrors v1)
  const continueDisabled =
    advancing ||
    (!isLast && itemCount === 0) ||
    (!isLast && exceeded) ||
    (stepId === 'details' && (!termsAccepted || !detailsValid)) ||
    (stepId === 'story' && anyStoryWritten && !storyConsent)
  const ctaLabel = isLast
    ? t('rail.done')
    : stepId === 'shipping'
      ? t('rail.confirmTradeIn')
      : t('rail.continue')

  // A step can register a pre-navigation action (e.g. Details saves the address); only advance if
  // it resolves true. Falling through with no action navigates immediately.
  const goNext = async () => {
    if (isLast) return
    const before = useCheckoutStore.getState().onBeforeNext
    if (before) {
      setAdvancing(true)
      try {
        if (!(await before())) return
      } finally {
        setAdvancing(false)
      }
    }
    // Scroll-to-top is driven by the route-change effect above (once per navigation).
    navigate(`/${steps[activeIndex + 1].path}`)
  }
  const goBack = () => {
    if (isFirst) return
    navigate(`/${steps[activeIndex - 1].path}`)
  }

  return (
    <>
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <span className="mark" aria-hidden>
              <IconLogo width={17} height={17} />
            </span>
            <span className="brand-copy">
              <span className="brand-title">{pageTitle || 'Trade-in'}</span>
              {pageSubtitle && <small>{pageSubtitle}</small>}
            </span>
          </div>
          {isFlowStep && !isLast && <Stepper steps={stepperSteps} activeIndex={activeIndex} />}
          <TopbarMenu onHowItWorks={() => setIntroOpen(true)} />
        </div>
      </header>

      {/* The terminal Confirm step (basket spent) and off-flow pages (My trade-ins) are full-width
          with no rail — they own their own content and actions. */}
      <main className={`stage ${isLast || !isFlowStep ? 'stage--full' : ''}`}>
        <section className="work">
          <Outlet />
        </section>
        {isFlowStep && !isLast && (
          <>
            {/* Desktop: sticky rail (hidden on narrow viewports). */}
            <aside className="summary">
              <BasketSummary
                ctaLabel={ctaLabel}
                ctaShowArrow={!isLast}
                continueDisabled={continueDisabled}
                showBack={!isFirst && !isLast}
                onContinue={goNext}
                onBack={goBack}
              />
            </aside>
            {/* Mobile: fixed bottom bar that expands into a sheet (self-hides on desktop). */}
            <MobileBasket
              ctaLabel={ctaLabel}
              ctaShowArrow={!isLast}
              continueDisabled={continueDisabled}
              showBack={!isFirst && !isLast}
              onContinue={goNext}
              onBack={goBack}
            />
          </>
        )}
      </main>

      {allowedCountry === false && <CountryWarningDialog />}
      <IntroDialog open={introOpen} onOpenChange={setIntroOpen} onAccept={acceptIntro} />
    </>
  )
}
